import { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { useToggle } from 'react-use';
import { useNavigate, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useActivationActions } from 'hooks/incident/useActivationActions';
import {
  filterOptions,
  type NotificationWithMetadataDto,
  useArchiveNotificationForAll,
  useNotificationsByGroup,
} from 'hooks/useNotification';
import {
  NotificationActionType,
  useAcknowledgeNotification,
  useArchiveNotification,
  useUserNotifications,
  type UserNotificationWithDetails,
} from 'hooks/useUserNotifications';
import { selectUserGuid, useAppSelector } from 'slices/commonSelectors';
import { HwcgActivationReply } from '../NotificationsTab/HwcgActivationReply';
import NewNotificationDrawer from '../NotificationsTab/NewNotificationDrawer';
import NotificationAttachedFiles from '../NotificationsTab/NotificationAttachedFiles';
import { MemphisThreatRichDetails } from '../NotificationsTab/MemphisThreatRichDetails';
import { UscgFocSoutheastThreatRichDetails } from '../NotificationsTab/UscgFocSoutheastThreatRichDetails';
import {
  isHardcodedMemphisThreatNotification,
  memphisCreateIncidentPrefill,
} from '../NotificationsTab/memphisThreatNotification';
import { isHardcodedEpaGulfThreatNotification } from '../NotificationsTab/epaGulfThreatNotification';
import { FemaThunderboltInviteActions } from '../NotificationsTab/FemaThunderboltInviteActions';
import {
  getFacilityDemoPack,
  isFacilityDemoNotificationId,
} from '../NotificationsTab/facilityDemoPack';
import { isHardcodedUscgFocSeThreatNotification } from '../NotificationsTab/uscgFocSoutheastThreatNotification';
import {
  formatNotificationTimestamp,
  isSitrepWorkflowNotification,
  NotificationSeverityBanner,
  ReviewerCommentsBlock,
} from '../NotificationsTab/notificationSeverityUi';
import ReviewRequestedLink from '../NotificationsTab/ReviewRequestedNavlink';
import { UserNotificationArchiveAction } from '../NotificationsTab/UserNotificationArchiveAction';
import {
  findFacilityDemoOrgEvent,
  findEpaGulfOrgEvent,
  findHeleneConeOrgEvent,
  findTnngMemphisOrgEvent,
  findUscgFocSeOrgEvent,
  useOrgEvents,
} from 'hooks/orgEvents/orgEventHooks';
import { isTropicalThreatNotification } from '../EventsTab/tropicalThresholdHelpers';
import {
  useTennesseeMemphisDemoEnabled,
  useTropicalDemoKind,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';
import { useSitStatAorScope } from './SitStatAorScopeContext';
import { scopeIncludesAnyAor } from './sitstat-aor-scope';

type NotificationsFilter = 'active' | 'historical' | 'sent';

/**
 * SitStat Notifications widget — same data/actions as the Notifications tab,
 * rendered with shadcn chrome for the dark SitStat rails.
 */
export function SitStatNotificationsWidget() {
  const demoKind = useTropicalDemoKind();
  const tennesseeMemphisDemo = useTennesseeMemphisDemoEnabled();
  const uscgFocEnabled = useUscgFocRequirementsFlag();
  const cfaEnabled = demoKind != null;
  const userGuid = useAppSelector(selectUserGuid);
  const { incidentId } = useParams<{ incidentId: string }>();
  const [filter, setFilter] = useState<NotificationsFilter>('active');
  const [searchText, setSearchText] = useState('');
  const [addOpen, toggleAddDrawer] = useToggle(false);
  const navigate = useNavigate();
  const { data: orgEvents = [] } = useOrgEvents({
    enabled: cfaEnabled || tennesseeMemphisDemo || uscgFocEnabled,
  });
  const facilityPack = getFacilityDemoPack(demoKind);
  const linkedEvent = uscgFocEnabled
    ? findUscgFocSeOrgEvent(orgEvents)
    : tennesseeMemphisDemo
      ? findTnngMemphisOrgEvent(orgEvents)
      : facilityPack
        ? findFacilityDemoOrgEvent(orgEvents, facilityPack)
        : demoKind === 'epa'
          ? findEpaGulfOrgEvent(orgEvents)
          : findHeleneConeOrgEvent(orgEvents);
  const openCreateIncident = () =>
    navigate('/create/incident', {
      state: memphisCreateIncidentPrefill(
        linkedEvent?.id,
        tennesseeMemphisDemo ? 'tnng' : 'cfa'
      ),
    });

  const {
    data: userNotifications = [],
    isLoading: isUserNotificationsLoading,
  } = useUserNotifications();
  const { data: notifications = [], isLoading: isNotificationsLoading } =
    useNotificationsByGroup();
  const acknowledgeNotificationMutation = useAcknowledgeNotification();
  const archiveNotificationForAllMutation = useArchiveNotificationForAll();
  const archiveUserNotification = useArchiveNotification();
  const { mutateAsync: handleActivationUpdate } = useActivationActions();
  const isLoading = isNotificationsLoading || isUserNotificationsLoading;
  const { scope: aorScope } = useSitStatAorScope();

  const filteredUserNotifications = useMemo(() => {
    let result = userNotifications.filter((un) =>
      filter === 'historical' ? un.archived : !un.archived
    );
    const query = searchText.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (item) =>
          (item.appMessage || '').toLowerCase().includes(query) ||
          (item.createdByName || '').toLowerCase().includes(query)
      );
    }
    if (incidentId) {
      result = result.filter(
        (n) =>
          isHardcodedMemphisThreatNotification(n.notificationId) ||
          isHardcodedEpaGulfThreatNotification(n.notificationId) ||
          isHardcodedUscgFocSeThreatNotification(n.notificationId) ||
          isFacilityDemoNotificationId(n.notificationId) ||
          n.incidentId === incidentId
      );
    }
    if (aorScope) {
      result = result.filter((n) => scopeIncludesAnyAor(aorScope, n.aors));
    }
    return result;
  }, [userNotifications, filter, searchText, incidentId, aorScope]);

  const filteredSentNotifications = useMemo(() => {
    let result = notifications.filter((n) => n.createdBy === userGuid);
    const query = searchText.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (item) =>
          (item.metadata?.emailSubject || '').toLowerCase().includes(query) ||
          (item.metadata?.emailMessage || '').toLowerCase().includes(query) ||
          (item.metadata?.appMessage || '').toLowerCase().includes(query) ||
          (item.metadata?.teamsMessage || '').toLowerCase().includes(query) ||
          (item.metadata?.smsMessage || '').toLowerCase().includes(query)
      );
    }
    if (incidentId) {
      result = result.filter((n) => n.incidentId === incidentId);
    }
    if (aorScope) {
      result = result.filter((n) => scopeIncludesAnyAor(aorScope, n.aors));
    }
    result.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return result;
  }, [notifications, searchText, userGuid, incidentId, aorScope]);

  const handleAcknowledgement = async (notificationId: string) => {
    if (
      isHardcodedMemphisThreatNotification(notificationId) ||
      isHardcodedEpaGulfThreatNotification(notificationId) ||
      isHardcodedUscgFocSeThreatNotification(notificationId) ||
      isFacilityDemoNotificationId(notificationId)
    )
      return;
    await acknowledgeNotificationMutation.mutateAsync({ notificationId });
  };

  const handleArchiveForAll = async (notificationId: string) => {
    if (
      isHardcodedMemphisThreatNotification(notificationId) ||
      isHardcodedEpaGulfThreatNotification(notificationId) ||
      isHardcodedUscgFocSeThreatNotification(notificationId) ||
      isFacilityDemoNotificationId(notificationId)
    )
      return;
    await archiveNotificationForAllMutation.mutateAsync({ notificationId });
  };

  const handleActivation = async (
    notification: UserNotificationWithDetails
  ) => {
    if (
      isHardcodedMemphisThreatNotification(notification.notificationId) ||
      isHardcodedEpaGulfThreatNotification(notification.notificationId) ||
      isHardcodedUscgFocSeThreatNotification(notification.notificationId) ||
      isFacilityDemoNotificationId(notification.notificationId)
    ) {
      return;
    }
    if (!notification.incidentId) return;
    await handleActivationUpdate({
      incidentId: notification.incidentId,
      memberUserGuid: userGuid,
      isActivated: true,
    });
    await archiveUserNotification.mutateAsync({
      notificationId: notification.notificationId,
    });
  };

  const renderUserNotification = (item: UserNotificationWithDetails) => {
    const isTropical = isTropicalThreatNotification(item, orgEvents);

    return (
      <Card
        key={item.notificationId}
        size="sm"
        className="tw:gap-0 tw:overflow-hidden tw:py-0"
      >
        <NotificationSeverityBanner
          priority={item.priority}
          label={isSitrepWorkflowNotification(item) ? 'Report' : undefined}
        />
        <CardContent className="tw:space-y-2 tw:px-3 tw:py-2.5">
          <div className="tw:flex tw:items-start tw:justify-between tw:gap-2">
            <p className="tw:text-xs tw:font-medium tw:leading-snug">
              {item.appMessage || 'Notification'}
            </p>
            <UserNotificationArchiveAction
              notificationId={item.notificationId}
              archived={item.archived}
            />
          </div>

          {isHardcodedUscgFocSeThreatNotification(item.notificationId) ? (
            <UscgFocSoutheastThreatRichDetails
              linkedOrgEventId={item.sourceItemId}
              onCreateIncident={openCreateIncident}
            />
          ) : isTropical ? (
            <MemphisThreatRichDetails
              linkedOrgEventId={item.sourceItemId}
              onCreateIncident={openCreateIncident}
              assetItemDensity="comfortable"
            />
          ) : null}
          {isSitrepWorkflowNotification(item) ? (
            <>
              <p className="tw:text-[11px] tw:text-muted-foreground">
                {item.createdByName || 'System'} ·{' '}
                {formatNotificationTimestamp(item.sentAt)}
              </p>
              <ReviewerCommentsBlock comments={item.reviewerComments} />
              {item.sourceItemId ? <ReviewRequestedLink {...item} /> : null}
            </>
          ) : (
            <>
              <p className="tw:text-[11px] tw:text-muted-foreground">
                {item.createdByName || 'System'} ·{' '}
                {formatNotificationTimestamp(item.sentAt)}
              </p>

              <FemaThunderboltInviteActions
                notificationId={item.notificationId}
                archived={item.archived}
              />
              {item.hwcgActivationRequest &&
              item.incidentId &&
              !item.acknowledged ? (
                <HwcgActivationReply
                  notificationId={item.notificationId}
                  incidentId={item.incidentId}
                  rosterActivation={item.hwcgActivationSource === 'roster'}
                  requestPeriodNumber={item.hwcgActivationPeriodNumber}
                />
              ) : null}
              <div className="tw:flex tw:flex-wrap tw:gap-1.5">
                {item.hwcgActivationRequest ? null : item.acknowledgementRequired &&
                  !item.acknowledged ? (
                  <Button
                    type="button"
                    size="xs"
                    onClick={() =>
                      void handleAcknowledgement(item.notificationId)
                    }
                  >
                    Confirm Receipt
                  </Button>
                ) : null}
                {item.hwcgActivationRequest ? null : item.actionType ===
                    NotificationActionType.ACTIVATION_REQUEST &&
                  !item.archivedAt ? (
                  <Button
                    type="button"
                    size="xs"
                    onClick={() => void handleActivation(item)}
                  >
                    Activate
                  </Button>
                ) : null}
              </div>
              {item.sourceItemId ? <ReviewRequestedLink {...item} /> : null}
              <NotificationAttachedFiles files={item.files} />
              {(item.acknowledged || item.archived) && (
                <p className="tw:text-[11px] tw:text-muted-foreground">
                  {item.acknowledged
                    ? `Confirmed ${formatNotificationTimestamp(item.acknowledgedAt)}`
                    : null}
                  {item.archived ? ' Archived' : null}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderSentNotification = (item: NotificationWithMetadataDto) => (
    <Card
      key={item.id}
      size="sm"
      className="tw:gap-0 tw:overflow-hidden tw:py-0"
    >
      <NotificationSeverityBanner priority={item.priority} />
      <CardContent className="tw:space-y-2 tw:px-3 tw:py-2.5">
        {item.metadata?.appMessage ? (
          <p className="tw:text-xs tw:font-medium tw:leading-snug">
            {item.metadata.appMessage}
          </p>
        ) : (
          <p className="tw:text-xs tw:font-medium tw:leading-snug">
            Notification sent
          </p>
        )}
        <p className="tw:text-[11px] tw:text-muted-foreground">
          Sent · {formatNotificationTimestamp(item.createdAt)}
        </p>
        {item.hasUnarchivedRecipients ? (
          <Button
            type="button"
            size="xs"
            onClick={() => void handleArchiveForAll(item.id)}
          >
            Archive for All Recipients
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );

  const list =
    filter === 'sent' ? filteredSentNotifications : filteredUserNotifications;

  return (
    <div className="tw:flex tw:max-h-[28rem] tw:flex-col tw:gap-2">
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <div className="tw:flex tw:flex-wrap tw:gap-1">
          {filterOptions.map((opt) => (
            <Button
              key={opt}
              type="button"
              size="xs"
              variant={filter === opt ? 'default' : 'outline'}
              className="tw:capitalize"
              onClick={() => setFilter(opt as NotificationsFilter)}
            >
              {opt}
            </Button>
          ))}
        </div>
        <Button
          type="button"
          size="icon-xs"
          variant="outline"
          aria-label="Add notification"
          title="Add notification"
          onClick={() => toggleAddDrawer(true)}
        >
          <Plus className="tw:h-3.5 tw:w-3.5" />
        </Button>
      </div>

      <div className="tw:relative tw:shrink-0">
        <Search className="tw:pointer-events-none tw:absolute tw:left-2 tw:top-1/2 tw:h-3.5 tw:w-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
        <Input
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search notifications…"
          className="tw:h-8 tw:pl-8 tw:text-xs"
          aria-label="Search notifications"
        />
      </div>

      <div className="tw:min-h-0 tw:flex-1 tw:space-y-2 tw:overflow-y-auto tw:pr-0.5">
        {isLoading ? (
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">Loading…</p>
        ) : list.length === 0 ? (
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            No notifications
          </p>
        ) : filter === 'sent' ? (
          (list as NotificationWithMetadataDto[]).map(renderSentNotification)
        ) : (
          (list as UserNotificationWithDetails[]).map(renderUserNotification)
        )}
      </div>

      {addOpen ? <NewNotificationDrawer toggle={toggleAddDrawer} /> : null}
    </div>
  );
}
